import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiCopy } from "react-icons/fi";
import { FaCheck, FaTimes, FaQuestion, FaChair, FaUsers, FaUser, FaTrash, FaChevronDown } from "react-icons/fa";
import RsvpDropdown from "./RsvpDropdown";
import TableDropdown from "./TableDropdown";

const capitalizeWords = (str) =>
    str?.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const rsvpConfig = (guest, t) => {
    if (!guest.rsvpDate) return { label: t('rsvpPending') || 'Nije odgovorio', color: 'bg-gray-100 text-gray-500 border-gray-200', dot: 'bg-gray-400', icon: <FaQuestion className="text-xs" /> };
    if (guest.isAttending) return { label: t('rsvpAttending') || 'Dolazi', color: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500', icon: <FaCheck className="text-xs" /> };
    return { label: t('rsvpNotAttending') || 'Ne dolazi', color: 'bg-red-50 text-red-600 border-red-200', dot: 'bg-red-400', icon: <FaTimes className="text-xs" /> };
};

const GuestCard = ({ guest, tables, onTableSelect, onDelete, onCopy, onRsvpChange }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const rsvp = rsvpConfig(guest, t);

    const registeredCompanions = guest.companions?.registered || [];
    const unregisteredCount = guest.companions?.unregistered || 0;
    const hasCompanions = registeredCompanions.length > 0 || unregisteredCount > 0;
    const totalGroup = 1 + registeredCompanions.length + unregisteredCount;

    const currentTableName = (() => {
        if (!guest.reservedTableId) return null;
        const id = guest.reservedTableId?._id || guest.reservedTableId;
        return tables.find(t => t._id === id)?.name || guest.reservedTableId?.name;
    })();

    return (
        <div className={`bg-white border rounded-2xl shadow-sm overflow-visible transition-all duration-200 ${expanded ? 'border-rose-200 shadow-md' : 'border-gray-200 hover:border-gray-300 hover:shadow'}`}>
            <div
                onClick={() => setExpanded(e => !e)}
                className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
            >
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${rsvp.dot}`} />
                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 truncate capitalize text-sm leading-tight">
                        {capitalizeWords(guest.fullName)}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${rsvp.color}`}>
                            {rsvp.icon} {rsvp.label}
                        </span>
                        {currentTableName && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 font-medium capitalize">
                                <FaChair className="text-xs" /> {currentTableName}
                            </span>
                        )}
                        {hasCompanions && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-500 border border-rose-100 font-medium">
                                <FaUsers className="text-xs" /> {totalGroup}
                            </span>
                        )}
                    </div>
                </div>
                <FaChevronDown className={`text-gray-400 text-sm flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
            </div>

            {expanded && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3 flex flex-col gap-3">
                    {hasCompanions && (
                        <div className="bg-rose-50 border border-rose-100 rounded-xl p-3">
                            <p className="text-xs font-semibold text-rose-600 mb-2 flex items-center gap-1.5">
                                <FaUsers className="text-xs" /> Grupa ({totalGroup} osoba)
                            </p>
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2 text-xs text-gray-700">
                                    <FaUser className="text-gray-400 flex-shrink-0" />
                                    <span className="capitalize font-medium">{capitalizeWords(guest.fullName)}</span>
                                    <span className="text-gray-400">(nositelj)</span>
                                </div>
                                {registeredCompanions.map((c) => (
                                    <div key={c._id || c} className="flex items-center gap-2 text-xs text-gray-700">
                                        <FaUser className="text-gray-400 flex-shrink-0" />
                                        <span className="capitalize">{capitalizeWords(c.fullName || String(c))}</span>
                                    </div>
                                ))}
                                {unregisteredCount > 0 && (
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <FaUser className="text-gray-400 flex-shrink-0" />
                                        <span>+{unregisteredCount} dodatna mjesta</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {guest.addedByGuestName && (
                        <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 text-xs text-blue-600">
                            Dodan/a od strane: <strong className="capitalize">{capitalizeWords(guest.addedByGuestName)}</strong>
                        </div>
                    )}

                    <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1.5">Status dolaska</p>
                        <RsvpDropdown guest={guest} onSelect={onRsvpChange} />
                    </div>

                    <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1.5">Premjesti za stol</p>
                        <TableDropdown guest={guest} tables={tables} onSelect={onTableSelect} />
                    </div>

                    <div className="flex gap-2 pt-1">
                        <button
                            onClick={() => onCopy(guest)}
                            className="flex-1 flex items-center justify-center gap-1.5 bg-gray-800 text-white text-xs font-medium py-2.5 rounded-xl hover:bg-gray-700 transition"
                        >
                            <FiCopy className="text-xs" /> Kopiraj link
                        </button>
                        <button
                            onClick={() => onDelete(guest._id)}
                            className="flex items-center justify-center w-10 bg-white border border-red-200 text-red-500 rounded-xl hover:bg-red-50 transition"
                        >
                            <FaTrash className="text-xs" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GuestCard;
